/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useDetectPhone from 'hooks/useDetectPhone'
import {
  setHasSession,
  setOtpVerificationRequired,
  setPhoneNumForOtpVerification,
} from 'store/shipping/shippingSlice'
import { setProfileUpdateScreen } from 'store/global/globalSlice'
import { useDispatch } from 'react-redux'
import {
  checkSession,
  verifyShipmentByToken,
  verifiyLoginByToken,
  verifyRegisterNewByToken,
  verifyRegisterUpdateByToken,
  verifyProfileUpdateByToken,
} from 'api/shipping'
import { PropTypes } from 'prop-types'
import { colors } from 'globalStyles'
import Card from 'react-bootstrap/Card'
import styled from 'styled-components'

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  margin-top: -80px;
  text-align: center;
`

const verificationCardContent = (respCode, t) => {
  switch (respCode) {
    case 0:
      return <LoadingContainer>{t('verification_messages.loading')}</LoadingContainer>
    case 400:
      return t('verification_messages.failed_request')
    case 404:
      return t('verification_messages.failed_request')
    case 409:
      return t('verification_messages.already_verified')
    default:
      return t('unsubscribe_messages.invalid_url')
  }
}

const VerificationCard = ({ token, type }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [respCode, setRespCode] = useState(0)
  const bpLgUnder = useDetectPhone()
  const dispatch = useDispatch()

  const unsubMsgStyle = {
    width: '100%',
    position: 'relative',
    border: 'none',
    flex: '1',
    marginLeft: '0px',
  }

  const lgUnderUnsubMsgStyle = {
    position: 'relative',
    border: 'none',
    flex: '1',
    marginLeft: '0px',
    marginRight: '15px',
    alignSelf: 'start',
  }

  const cardTitle = {
    fontWeight: '600px',
    fontSize: '20px',
    color: `${colors.text_link}`,
    marginTop: '80px',
  }

  const cardBodyStyle = {
    width: '100%',
  }

  useEffect(() => {
    async function shipmentFetchData() {
      try {
        const verifyResp = await verifyShipmentByToken(token)
        if (verifyResp.status === 200) {
          navigate(`/rs/tr/${verifyResp.data.language}/${verifyResp.data.shipmentHashedPin}`, {
            replace: true,
          })
        }
      } catch (error) {
        // handle error
        if (error.response.status === 401) {
          const shipmentPinVal = error.response.data.shipmentPin
          const expiredTokenVal = error.response.data.expiredToken
          navigate(
            `/rs/tr/${error.response.data.language}/${error.response.data.shipmentHashedPin}/verifyLinkExpired`,
            {
              state: {
                shipmentPin: shipmentPinVal,
                expiredToken: expiredTokenVal,
              },
            }
          )
        }
        if (error.response && error.response.status) {
          setRespCode(error.response.status)
        }
      }
    }

    async function loginFetchData() {
      try {
        const verifyResp = await verifiyLoginByToken(token)
        if (verifyResp.status === 200) {
          try {
            const resp = await checkSession()
            if (resp.status === 200) {
              dispatch(setHasSession(true))
              if (resp.data.isOtpVerificationRequired) {
                dispatch(setOtpVerificationRequired(true))
                dispatch(setPhoneNumForOtpVerification(resp.data.phoneNumber))
              } else {
                dispatch(setOtpVerificationRequired(false))
                dispatch(setPhoneNumForOtpVerification(''))
              }
            } else {
              dispatch(setHasSession(false))
            }
          } catch (error) {
            dispatch(setHasSession(false))
          }
          navigate(`/rs/tr/${verifyResp.data.language}/recipient`, {
            replace: true,
          })
        }
      } catch (error) {
        if (error.response && error.response.status) {
          setRespCode(error.response.status)
        }
      }
    }

    async function registerNewFetchData() {
      try {
        const verifyResp = await verifyRegisterNewByToken(token)
        if (verifyResp.status === 200) {
          navigate(`/rs/tr/${verifyResp.data.language}/login`, {
            replace: true,
          })
        }
      } catch (error) {
        if (error.response && error.response.status) {
          setRespCode(error.response.status)
        }
      }
    }

    async function registerUpdateFetchData() {
      try {
        const verifyResp = await verifyRegisterUpdateByToken(token)
        if (verifyResp.status === 200) {
          navigate(`/rs/tr/${verifyResp.data.language}/login`, {
            replace: true,
          })
        }
      } catch (error) {
        if (error.response && error.response.status) {
          setRespCode(error.response.status)
        }
      }
    }

    async function updateProfileFetchData() {
      try {
        const verifyResp = await verifyProfileUpdateByToken(token)
        if (verifyResp.status === 200) {
          dispatch(setHasSession(false))
          dispatch(setProfileUpdateScreen(true))
          navigate(`/rs/tr/${verifyResp.data.language}/login`, {
            replace: true,
          })
        }
      } catch (error) {
        if (error.response && error.response.status) {
          setRespCode(error.response.status)
        }
      }
    }

    if (type === 'login') loginFetchData()
    else if (type === 'register-new') registerNewFetchData()
    else if (type === 'register-update') registerUpdateFetchData()
    else if (type === 'profile-update') updateProfileFetchData()
    else shipmentFetchData()
  }, [token, navigate, type, dispatch])

  return (
    <Card style={bpLgUnder ? lgUnderUnsubMsgStyle : unsubMsgStyle}>
      <Card.Body style={cardBodyStyle}>
        <Card.Title style={cardTitle}>{verificationCardContent(respCode, t)}</Card.Title>
      </Card.Body>
    </Card>
  )
}

VerificationCard.propTypes = {
  token: PropTypes.string,
  type: PropTypes.string,
}

VerificationCard.defaultProps = {
  token: null,
  type: '',
}

export default VerificationCard
